import { FlowStep, UpsertFlowStepInput } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import HowDoYouGetPaidSelection from 'features/onboarding/components/HowDoYouGetPaidSelection/HowDoYouGetPaidSelection';
import useGetFlowStepId from 'features/onboarding/hooks/useGetFlowStepId';
import useHowDoYouGetPaid from 'features/onboarding/hooks/useHowDoYouGetPaid';
import useSaveHowDoYouGetPaidSelection from 'features/onboarding/hooks/useSaveHowDoYouGetPaidSelection';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';

const HowDoYouGetPaidScreen = () => {
  const router = useRouter();
  const {
    successData,
    isLoading: isQueryLoading,
    hasError,
  } = useHowDoYouGetPaid();
  const { saveSelection, isSaving } = useSaveHowDoYouGetPaidSelection();
  const flowStepId = useGetFlowStepId();
  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();
  const { t: translatedText } = useTranslation('onboarding');
  const { defaultMessage, graphqlErrorsTitle } = useDefaultErrorStrings();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/onboarding/paycheck-option/payCheckOption',
  });

  const handleContinueButton = async (selectedOption: string | undefined) => {
    try {
      if (selectedOption) {
        await saveSelection(selectedOption);
      }

      const input: UpsertFlowStepInput = {
        id: flowStepId,
        intentBasedOnboarding: {
          didSkip: !selectedOption,
        },
      };

      const result = await submitUpsertFlowStep({ input });
      if (!result.error) {
        const flowStep = result.data?.upsertFlowStep as FlowStep;
        redirectUserToTargetRoute(flowStep);
      }
    } catch (err) {
      if (err instanceof AbortError) {
        return;
      }
      throw err;
    }
  };

  const optionsData =
    successData?.options.map((option) => ({
      id: option.id,
      labelText: option.title,
      clickAnalytics: option.clickAnalytics ?? null,
    })) ?? [];

  return (
    <>
      {successData && (
        <HowDoYouGetPaidSelection
          optionsData={optionsData}
          headlineText={successData.title}
          bodyText={successData.subtitle}
          onContinueButton={handleContinueButton}
          isLoading={isQueryLoading || isUpsertFlowStepLoading || isSaving}
        />
      )}

      <ScreenTransitionOverlay
        isLoading={isQueryLoading || isUpsertFlowStepLoading || isSaving}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('loading.titleText')}
          />
        }
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={graphqlErrorsTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: translatedText('errors.goToHomePageButtonLabel'),
                onPress: () => {
                  router.push('/');
                },
              }}
            />
          ) : null
        }
      />
    </>
  );
};
export default HowDoYouGetPaidScreen;
